import { useEffect } from 'react';

import type { MatchedLocation } from '../lib/api.js';

/**
 * The location pack — what a producer sends to their team or a host.
 *
 * PRINTED, NOT GENERATED SERVER-SIDE, and that is a deliberate choice rather
 * than a shortcut. This monorepo ships no PDF library in any of its six
 * services; adding one would mean a dependency, a rendering engine to keep
 * patched, fonts to embed, and a second layout to maintain in lockstep with
 * this one. The browser already has all of that and produces a genuine,
 * paginated, selectable-text PDF through Save as PDF. What it does not give is
 * a file the server can email unattended — if that is needed, it is a different
 * feature and a real dependency, not a tweak to this one.
 *
 * WHAT GOES IN IT IS WHAT WAS DECIDED, not what was found. The pack carries the
 * shortlist: a producer sending forty machine-suggested candidates to a host
 * has sent noise, and the recipient cannot tell which ones anybody actually
 * looked at.
 *
 * EVERY CLAIM CARRIES ITS SOURCE. Coverage says its denominator, an unassessed
 * candidate says so, and the axis reasoning travels with the state — because
 * this document leaves the product and will be read by someone who cannot click
 * through to the evidence.
 */
export function LocationPack({
  projectTitle,
  scriptVersionLabel,
  locations,
  onClose,
  onPrint,
}: {
  projectTitle: string;
  scriptVersionLabel: string;
  locations: readonly MatchedLocation[];
  onClose: () => void;
  /** Called with the number of locations actually in the file — pins excluded. */
  onPrint?: (locationCount: number) => void;
}) {
  // Escape closes it. A full-screen overlay with only a button to leave is a
  // trap on a keyboard, and this one covers the whole application.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const printed = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  /**
   * A PIN IS NOT IN THE PACK (ADR 0012).
   *
   * The server already coarsens a point of interest before it is sent — derived
   * label, no photograph, no address, no rate — so including one here does not
   * leak anything. It produces something worse in its own way: a page headed
   * "Point of interest" with no picture, no place and no price, in a document a
   * producer emails to a host or a line producer. A pin is a scout's lead, not
   * a location anybody can be sent to look at; the pack is for the ones that
   * can. They are named in the footnote rather than dropped silently, because a
   * producer who shortlisted one needs to know why it is not in the file they
   * are about to send.
   */
  const bookable = locations.filter((location) => location.sourceType !== 'POI');
  const pins = locations.length - bookable.length;

  return (
    <div
      data-testid="location-pack"
      className="pack-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Location pack"
    >
      <div className="pack-controls">
        <button
          type="button"
          className="btn btn-primary"
          data-testid="pack-print"
          onClick={() => {
            onPrint?.(bookable.length);
            window.print();
          }}
        >
          Save as PDF
        </button>
        <button type="button" className="btn btn-ghost" data-testid="pack-close" onClick={onClose}>
          Close
        </button>
        <span className="mm" style={{ fontSize: 12 }}>
          Your browser’s print dialog — choose “Save as PDF” as the destination.
        </span>
      </div>

      <article className="pack">
        <header style={{ borderBottom: '2px solid var(--ink)', paddingBottom: 12 }}>
          <p className="flbl" style={{ margin: 0, letterSpacing: '0.08em' }}>
            Location pack
          </p>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 30, margin: '6px 0 0' }}>
            {projectTitle}
          </h1>
          <p className="mm" style={{ fontSize: 12, margin: '6px 0 0' }}>
            {scriptVersionLabel} · {bookable.length} location
            {bookable.length === 1 ? '' : 's'} · prepared {printed}
          </p>
        </header>

        {bookable.length === 0 ? (
          <p style={{ marginTop: 20, fontSize: 13 }} data-testid="pack-empty">
            Nothing is shortlisted yet. Shortlist the locations you want in the pack and export
            again — a pack of everything that was suggested is not a pack.
          </p>
        ) : (
          bookable.map((location) => (
            <section
              key={`${location.sourceType}:${String(location.sourceId)}`}
              className="pack-item"
              data-testid={`pack-item-${String(location.sourceId)}`}
            >
              <h2 style={{ fontSize: 18, margin: 0 }}>{location.title}</h2>
              <p className="mm" style={{ fontSize: 12, margin: '4px 0 10px' }}>
                {[
                  location.typeCode,
                  [location.city, location.countryIso].filter(Boolean).join(', ') || null,
                  location.priceOnRequest
                    ? 'Price on request'
                    : location.rateAmount === null
                      ? null
                      : `From ${String(location.rateAmount)}/day`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>

              {location.imageUrl === null ? null : (
                <img
                  src={location.imageUrl}
                  alt=""
                  loading="lazy"
                  style={{
                    width: '100%',
                    maxHeight: 260,
                    objectFit: 'cover',
                    border: '1px solid var(--color-divider)',
                  }}
                />
              )}

              {location.requirements.map((r) => (
                <div key={r.candidateId} style={{ marginTop: 12 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>
                    {r.title}
                    {r.assessed && r.coverage.total > 0 ? (
                      <span className="mm" style={{ fontWeight: 400 }}>
                        {' '}
                        — {r.coverage.verified} of {r.coverage.total} must-haves covered
                      </span>
                    ) : (
                      <span className="mm" style={{ fontWeight: 400 }}>
                        {' '}
                        — added by hand, not assessed
                      </span>
                    )}
                  </p>

                  {Object.entries(r.axes).length === 0 ? null : (
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12 }}>
                      {Object.entries(r.axes).map(([axis, verdict]) => (
                        <li key={axis}>
                          <strong>{verdict.state.toLowerCase()}</strong> — {verdict.reasoning}
                        </li>
                      ))}
                    </ul>
                  )}

                  {r.scenes.length === 0 ? null : (
                    <p className="mm" style={{ fontSize: 12, margin: '6px 0 0' }}>
                      Scenes:{' '}
                      {r.scenes
                        .map((s) => `${s.sceneNumber ?? '—'} ${s.slugline ?? ''}`.trim())
                        .join(' · ')}
                    </p>
                  )}
                </div>
              ))}
            </section>
          ))
        )}

        <footer style={{ marginTop: 24, borderTop: '1px solid var(--color-divider)', paddingTop: 10 }}>
          {pins === 0 ? null : (
            <p className="mm" style={{ fontSize: 11, margin: '0 0 6px' }} data-testid="pack-pins-excluded">
              {pins} shortlisted {pins === 1 ? 'find is' : 'finds are'} a scout&rsquo;s point of
              interest rather than a listed location, and {pins === 1 ? 'is' : 'are'} not included
              here — {pins === 1 ? 'it has' : 'they have'} no owner, no rate and no photographs to
              send. Ask your scout to bring {pins === 1 ? 'it' : 'them'} onto the platform first.
            </p>
          )}
          <p className="mm" style={{ fontSize: 11, margin: 0 }}>
            Prepared in ScoutAround from the script breakdown. Availability and rates are
            indicative until confirmed with the host.
          </p>
        </footer>
      </article>
    </div>
  );
}
